import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiLoader, FiSave } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';

const emptyForm = { employee_id: '', amount: '', date: new Date().toISOString().slice(0, 10), paid_via: 'cash', account_id: '', notes: '' };

export default function AdvanceForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [employees, setEmployees] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    Promise.all([
      api.employees.list({ per_page: 500 }).then(({ data }) => {
        const paginated = data.employees || data.data || data;
        const list = paginated?.data || paginated;
        setEmployees(Array.isArray(list) ? list : []);
      }),
      api.accounts.list({ per_page: 500 }).then(({ data }) => {
        const paginated = data.accounts || data.data || data;
        const list = paginated?.data || paginated;
        setAccounts(Array.isArray(list) ? list : []);
      }),
    ]);
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const errs = {};
    if (!form.employee_id) errs.employee_id = 'Employee is required';
    if (!form.amount || parseFloat(form.amount) <= 0) errs.amount = 'Amount must be greater than 0';
    if (!form.date) errs.date = 'Date is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.advances.create({
        employee_id: parseInt(form.employee_id),
        amount: parseFloat(form.amount),
        date: form.date,
        paid_via: form.paid_via,
        account_id: form.account_id ? parseInt(form.account_id) : undefined,
        notes: form.notes || undefined,
      });
      toast('success', '', 'Advance recorded successfully');
      navigate('/advances');
    } catch (err) {
      showError('Failed to record advance', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Give Advance"
        subtitle="Record an advance payment to an employee."
        crumbs={[{ label: 'HR', to: '/advances' }, { label: 'Advances', to: '/advances' }, { label: 'New Advance' }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/advances')}><FiArrowLeft /> Back</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><FiLoader /> Saving...</> : <><FiSave /> Record Advance</>}
            </button>
          </>
        }
      />

      <div className="card">
        <div className="card-header"><div className="card-title">Advance Details</div></div>
        <div className="card-body">
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Employee <span className="req">*</span></label>
              <SearchableSelect
                options={employees.map((e) => ({ value: e.id, label: `${e.name} (${e.employee_no})` }))}
                value={form.employee_id}
                onChange={(v) => { set('employee_id', v); if (errors.employee_id) setErrors((p) => ({ ...p, employee_id: '' })); }}
                isInvalid={!!errors.employee_id}
                placeholder="-- Select employee --"
              />
              {errors.employee_id && <div className="invalid-feedback">{errors.employee_id}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Amount (PKR) <span className="req">*</span></label>
              <input type="number" className={`form-control ${errors.amount ? 'is-invalid' : ''}`} value={form.amount} onChange={(e) => { set('amount', e.target.value); if (errors.amount) setErrors((p) => ({ ...p, amount: '' })); }} placeholder="0.00" min="0.01" step="0.01" />
              {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Date <span className="req">*</span></label>
              <input type="date" className={`form-control ${errors.date ? 'is-invalid' : ''}`} value={form.date} onChange={(e) => { set('date', e.target.value); if (errors.date) setErrors((p) => ({ ...p, date: '' })); }} />
              {errors.date && <div className="invalid-feedback">{errors.date}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Paid Via <span className="req">*</span></label>
              <select className="form-control" value={form.paid_via} onChange={(e) => set('paid_via', e.target.value)}>
                <option value="cash">Cash</option>
                <option value="bank">Bank</option>
              </select>
            </div>
          </div>
          {form.paid_via === 'bank' && (
            <div className="form-group">
              <label className="form-label">Bank Account</label>
              <select className="form-control" value={form.account_id} onChange={(e) => set('account_id', e.target.value)}>
                <option value="">Select bank account</option>
                {accounts.filter((a) => a.parent_id === 207).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional notes..." />
          </div>
        </div>
      </div>
    </div>
  );
}
